import { Fragment, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PUBLIC_CHANNELS, type ChannelType, type ConversationDetailDTO, type MessageDTO } from '@sac/contracts'
import { isDangerousFile, mimeOfDataUrl } from '@sac/contracts'
import { ChannelMark, Icon } from '../icons'
import { IdentityMatch } from './IdentityMatch'
import { QuestionHistory } from './QuestionHistory'
import { Lightbox } from './Lightbox'
import { AgentAvatar } from '../AgentAvatar'
import { RichText } from './RichText'
import { CustomerAvatar } from '../CustomerAvatar'
import { api } from '../../lib/api'
import { qk } from '../../lib/query'

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

/** Dia e hora por extenso, para o `title` — a resposta exata a "quando foi isso?". */
function fmtCompleto(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'full', timeStyle: 'short' })
}

const diaDe = (iso: string) => new Date(iso).toDateString()

/**
 * O rótulo de um dia: "Hoje", "Ontem", ou a data por extenso.
 *
 * O ano só aparece quando não é o atual. Uma caixa de entrada é quase toda de hoje e ontem, e
 * repetir "de 2026" em toda conversa é ruído — mas uma conversa de dezembro passado, lida em
 * janeiro, sem ano, é pior que não ter data nenhuma.
 */
function fmtDia(iso: string): string {
  const d = new Date(iso)
  const hoje = new Date()
  const ontem = new Date(hoje)
  ontem.setDate(hoje.getDate() - 1)
  if (d.toDateString() === hoje.toDateString()) return 'Hoje'
  if (d.toDateString() === ontem.toDateString()) return 'Ontem'
  return d.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    ...(d.getFullYear() === hoje.getFullYear() ? {} : { year: 'numeric' }),
  })
}

/** Uma faixa por dia, no mesmo desenho do separador de canal que já existe logo abaixo. */
/**
 * O anexo do Mercado Livre. Ele não traz URL: o arquivo só sai com o token do vendedor, então vem
 * pela nossa API. Sem URL quando o próprio ML o marca como ameaça.
 */
type MlAttachment = { original_filename?: string | null; type?: string | null; potential_security_threat?: boolean }
const mlAttachmentOf = (m: MessageDTO) => (m.metadata as { mlAttachments?: MlAttachment[] } | null)?.mlAttachments?.[0]
function mlAttachmentUrl(m: MessageDTO): string | undefined {
  const ml = mlAttachmentOf(m)
  return ml && !ml.potential_security_threat ? `/api/messages/${m.id}/attachment` : undefined
}

function DaySeparator({ iso }: { iso: string }) {
  return (
    <div className="sep dia">
      <span className="ln" />
      <span className="lb">{fmtDia(iso)}</span>
      <span className="ln" />
    </div>
  )
}

function MessageRow({
  m,
  onRetry,
  onOpenImage,
  customerId,
  customerColor,
  customerName,
  agentColor,
}: {
  m: MessageDTO
  onRetry: (messageId: string) => void
  onOpenImage: (url: string, caption: string | null) => void
  customerId: string
  customerColor: string
  customerName: string
  /** Cor do agente que escreveu, para as iniciais quando ele ainda não subiu foto. */
  agentColor?: string
}) {
  if (m.messageType === 'system_event') {
    const meta = m.metadata as { kind?: string; channel?: string }
    if (meta.kind === 'channel_separator' && meta.channel) {
      return (
        <div className="sep">
          <span className="ln" />
          <span className="lb">
            <ChannelMark channel={meta.channel as ChannelType} size={14} />
            {m.text}
          </span>
          <span className="ln" />
        </div>
      )
    }
    return (
      <div className="ev">
        <span title={fmtCompleto(m.createdAt)}>{fmtTime(m.createdAt)}</span> · <b>{m.text}</b>
      </div>
    )
  }

  if (m.messageType === 'internal_note') {
    return (
      <div className="note-row">
        <div className="note">
          <div className="nh">
            <Icon name="lock" size={11} />
            Nota interna · {m.senderAgentName ?? ''}
          </div>
          {m.text}
          <div className="meta" style={{ textAlign: 'right', fontSize: 10.5, opacity: 0.7, marginTop: 4 }} title={fmtCompleto(m.createdAt)}>
            {fmtTime(m.createdAt)}
          </div>
        </div>
      </div>
    )
  }

  const out = m.direction === 'outgoing'
  // `senderType` nunca chega como 'ai' pelo caminho normal — quem envia é sempre uma pessoa.
  // O que marca a bolha é `aiVerbatim`: a sugestão saiu sem uma letra mudada.
  const isAi = m.senderType === 'ai' || (m.metadata as { aiVerbatim?: boolean })?.aiVerbatim === true
  let body: React.ReactNode = m.text ? <RichText text={m.text} /> : m.text

  if (m.messageType === 'order') {
    // Shopee sends the order as a card with only an order_sn — no text at all. Rendering the
    // number is still far better than the blank bubble this used to be, and it tells the agent
    // which order the next message is about.
    const content = (m.metadata as { content?: { order_sn?: string } }).content
    const sn = content?.order_sn
    if (sn) {
      body = (
        <div className="pcard">
          <span className="pthumb">🧾</span>
          <span>
            <span style={{ fontSize: 12.5, fontWeight: 600 }}>Pedido compartilhado</span>
            <br />
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{sn}</span>
          </span>
        </div>
      )
    }
  }

  /**
   * Figurinha da Shopee: do tamanho de um emoji, que é o que ela é.
   *
   * Desenhada antes do ramo de imagem e sem moldura de bolha — um "joinha" ocupando a largura
   * de uma foto fazia uma reação parecer um documento.
   */
  if (m.messageType === 'sticker') {
    const c = (m.metadata as { content?: { image_url?: string; url?: string } })?.content
    const url = c?.image_url ?? c?.url
    body = url ? (
      <img src={url} alt="figurinha" loading="lazy" className="tl-sticker" />
    ) : (
      <span className="tl-media-note">🙂 figurinha</span>
    )
  }

  if (m.messageType === 'image') {
    // TWO url keys, not one, and the difference is invisible until an image renders blank.
    // Measured across production: `image` carries `content.url` (25 messages), while `sticker`
    // (9) and `image_with_text` (4) carry `content.image_url` instead. All three arrive here as
    // messageType 'image' via the adapter's type map, so reading only `url` silently blanked a
    // third of them — including photos the customer sent WITH a question attached.
    //
    // WhatsApp (both adapters) writes `content.url`, since its images are downloaded at ingest
    // and stored as data URIs. Reading both keys keeps this branch channel-agnostic, and fixes
    // the already-stored history without a backfill.
    const content = (m.metadata as { content?: { url?: string; image_url?: string } })?.content
    const url = content?.url ?? content?.image_url ?? mlAttachmentUrl(m)
    body = url ? (
      <>
        <img
          src={url}
          alt="Imagem enviada"
          loading="lazy"
          className="tl-img"
          title="Clique para ampliar"
          onClick={() => onOpenImage(url, m.text)}
          style={{ maxWidth: 220, maxHeight: 220, borderRadius: 8, display: 'block', cursor: 'zoom-in' }}
        />
        {m.text && <div style={{ marginTop: 4 }}><RichText text={m.text} /></div>}
      </>
    ) : (
      <span style={{ fontStyle: 'italic', color: 'var(--text-3)' }}>📷 Imagem</span>
    )
  }

  /**
   * Voice notes, clips and files.
   *
   * All three used to arrive as nothing: audio was never even looked for at ingest, and a
   * caption-less video fell through a ternary that produced an image with no source. A customer
   * recording twenty seconds about a wall they cannot describe in writing is worth more than the
   * text they did not write.
   *
   * Rendered with the browser's own player rather than a component: it already handles seeking,
   * speed and the scrubber, and none of that is worth reimplementing.
   */
  if (m.messageType === 'audio' || m.messageType === 'video' || m.messageType === 'document') {
    const meta = (m.metadata ?? {}) as {
      content?: { url?: string }
      mediaNote?: string
      fileName?: string
      seconds?: number
      quarantined?: { deletedFromWhatsApp?: boolean }
    }
    /**
     * Arquivo perigoso: nunca um link de download. Vale pelo marcador do servidor E pela própria
     * regra aplicada aqui — um `.js` que chegou antes da quarentena existir, e que a varredura
     * ainda não alcançou, também não pode ser oferecido.
     */
    const ml = mlAttachmentOf(m)
    const fileName = meta.fileName ?? ml?.original_filename ?? undefined
    const perigoso =
      m.messageType === 'document' &&
      (Boolean(meta.quarantined) ||
        Boolean(ml?.potential_security_threat) ||
        isDangerousFile(fileName, mimeOfDataUrl(meta.content?.url) ?? ml?.type))
    const url = perigoso ? undefined : (meta.content?.url ?? mlAttachmentUrl(m))
    const dur = meta.seconds ? ` · ${Math.floor(meta.seconds / 60)}:${String(meta.seconds % 60).padStart(2, '0')}` : ''

    body = (
      <>
        {m.messageType === 'audio' && url && <audio controls preload="metadata" src={url} className="tl-audio" />}
        {m.messageType === 'video' && url && (
          <video controls preload="metadata" src={url} className="tl-video" onClick={(e) => e.stopPropagation()} />
        )}
        {/* Um documento não toca nem se exibe — ele se ABRE. Esta bolha ficava completamente
            vazia quando o arquivo existia: nenhum dos players cobria `document`, e a nota abaixo
            só aparece quando NÃO há arquivo. Ou seja, quanto melhor o caso, menos se via. */}
        {m.messageType === 'document' && url && (
          <a
            className="tl-file"
            href={url}
            download={fileName ?? 'arquivo'}
            // O anexo é um data URI na própria mensagem; abrir noutra aba mostraria a URL
            // gigante e nada mais, então o caminho é baixar com o nome que o cliente mandou.
            onClick={(e) => e.stopPropagation()}
            title={`Baixar ${fileName ?? 'arquivo'}`}
          >
            <span className="tl-file-ico">📎</span>
            <span className="tl-file-name">{fileName ?? 'Arquivo'}</span>
            <span className="tl-file-act">Baixar</span>
          </a>
        )}
        {perigoso && (
          <div className="tl-danger" role="alert">
            <b>⚠ Arquivo perigoso bloqueado</b>
            <span className="tl-danger-name">{fileName ?? 'arquivo'}</span>
            <span>
              Não é uma nota fiscal: é um programa disfarçado. Não peça ao cliente para reenviar e
              não abra se ele chegar por outro caminho.
              {meta.quarantined?.deletedFromWhatsApp ? ' Já foi apagado do WhatsApp.' : ''}
            </span>
          </div>
        )}
        {/* No player: either the file was too big to inline or the download failed. Saying so is
            the point — a thread that silently omits "cliente mandou um áudio" is worse. */}
        {!url && !perigoso && (
          <span className="tl-media-note">
            {m.messageType === 'audio' ? '🎤' : m.messageType === 'video' ? '🎬' : '📎'}{' '}
            {meta.mediaNote ?? fileName ?? 'Anexo recebido'}
            {dur}
          </span>
        )}
        {url && m.messageType !== 'document' && dur && <div className="tl-media-dur">{dur.replace(' · ', '')}</div>}
        {m.text && <div style={{ marginTop: 4 }}><RichText text={m.text} /></div>}
      </>
    )
  }

  if (m.messageType === 'product') {
    /**
     * O cartão de produto, desenhado como a Shopee o desenha: foto, nome em duas linhas, preço
     * "por" e o preço "de" riscado. É o que o comprador está vendo do lado dele, e o atendente
     * precisa ver o mesmo para responder sobre o mesmo produto.
     *
     * Três formatos chegam aqui, e todos são lidos em vez de presumidos: o resolvido pela API
     * (`metadata.product`, com foto e faixas de preço), o cartão de variação que a própria Shopee
     * descreve no payload (mesma chave), e o antigo do simulador, plano, com um preço e um emoji.
     */
    const flat = m.metadata as { name?: string; sku?: string; price?: number; emoji?: string }
    const p = (m.metadata as { product?: Record<string, unknown> }).product
    const content = (m.metadata as { content?: { item_id?: number; shop_id?: number } }).content

    const name = (p?.name as string | undefined) ?? flat.name ?? m.text ?? 'Produto'
    const sku = (p?.sku as string | undefined) ?? flat.sku ?? null
    const imageUrl = (p?.imageUrl as string | undefined) ?? null
    const unlisted = typeof p?.status === 'string' && p.status !== 'NORMAL'
    const itemId = content?.item_id ?? (content as { product_id?: number } | undefined)?.product_id ?? (p?.itemId as number | undefined)
    const href = itemId && content?.shop_id ? `https://shopee.com.br/product/${content.shop_id}/${itemId}` : null
    const variation = p?.variationName as string | undefined

    // Igual ao Seller Center: "R$49,90 - R$74,90", e um preço só quando a faixa colapsa.
    const brl = (n: number) => `R$${n.toFixed(2).replace('.', ',')}`
    const faixa = (lo: unknown, hi: unknown) => {
      const a = typeof lo === 'number' ? lo : null
      const b = typeof hi === 'number' ? hi : null
      if (a === null && b === null) return null
      if (a === null || b === null || a === b) return brl((a ?? b)!)
      return `${brl(a)} - ${brl(b)}`
    }
    const preco = faixa(p?.priceMin, p?.priceMax) ?? (flat.price != null ? brl(flat.price) : null)
    const precoDe = faixa(p?.originalMin, p?.originalMax)

    body = (
      <div className="pc">
        <div className="pc-k">Produto</div>
        <div className="pc-row">
          {imageUrl ? (
            <img className="pc-img" src={imageUrl} alt="" loading="lazy" />
          ) : (
            <span className="pc-img pc-ph">{flat.emoji ?? '📦'}</span>
          )}
          <div className="pc-info">
            <div className="pc-name" title={name}>
              {name}
            </div>
            {variation && <div className="pc-var">{variation}</div>}
            {(preco || precoDe) && (
              <div className="pc-price">
                {preco && <span className="pc-now">{preco}</span>}
                {precoDe && <s className="pc-was">{precoDe}</s>}
              </div>
            )}
            {(sku || unlisted || href) && (
              <div className="pc-meta">
                {sku && <span>{sku}</span>}
                {unlisted && <span className="pc-warn">{String(p?.status)}</span>}
                {href && (
                  <a href={href} target="_blank" rel="noopener noreferrer">
                    Ver na Shopee
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const statusMark =
    out && !isAi ? (
      m.status === 'failed' ? (
        <span style={{ color: 'var(--danger)' }}> Falha</span>
      ) : m.status === 'read' ? (
        <span style={{ color: '#4b57f5' }}> ✓✓</span>
      ) : m.status === 'delivered' ? (
        <span style={{ color: 'var(--text-3)' }}> ✓✓</span>
      ) : m.status === 'sent' ? (
        <span style={{ color: 'var(--text-3)' }}> ✓</span>
      ) : (
        <span style={{ color: 'var(--text-3)' }}> …</span>
      )
    ) : null

  /**
   * Quem escreveu, ancorado no pé da bolha.
   *
   * A linha de meta já trazia o nome do agente, mas só nas mensagens que saíram e só em texto —
   * numa conversa com três pessoas atendendo, saber de quem é cada fala exigia ler. Um círculo
   * na mesma posição em toda bolha responde isso de relance.
   *
   * O cliente ganha o avatar gerado que a caixa de entrada já usa; nós, a foto de quem escreveu,
   * com as iniciais quando ainda não há foto. A IA fica com a própria marca — ela não é ninguém,
   * e dar um rosto a ela seria a última coisa que esta interface deveria fazer.
   */
  const avatar = isAi ? (
    <img
      className="row-av ai"
      src="/bot-avatar.png"
      alt=""
      title="Sugestão da IA, enviada sem edição por um humano"
    />
  ) : out ? (
    <AgentAvatar
      className="row-av"
      name={m.senderAgentName}
      color={agentColor}
      avatarUrl={m.senderAgentAvatarUrl}
      size={25}
    />
  ) : (
    <CustomerAvatar
      className="row-av"
      seed={customerId}
      color={customerColor}
      name={customerName}
      size={25}
    />
  )

  return (
    <div className={`row ${out ? 'out' : 'in'} ${isAi ? 'ai' : ''}`}>
      {/* Do lado de DENTRO da bolha, não do lado da margem.
          A bolha do cliente encosta na esquerda, então o rosto dele fica à direita dela; a nossa
          encosta na direita, então o nosso fica à esquerda. Assim os dois rostos ficam juntos no
          meio da conversa, que é para onde os olhos já vão — encostados nas bordas da tela eles
          ficam a uma largura de bolha de distância um do outro. */}
      {out && avatar}
      <div className="bub">
        {body}
        <div className="meta" title={fmtCompleto(m.createdAt)}>
          {out ? (isAi ? '✨ IA' : m.senderAgentName ?? '') : ''}
          {out ? ' · ' : ''}
          {fmtTime(m.createdAt)}
          {statusMark}
        </div>
        {m.status === 'failed' && (
          <div style={{ textAlign: 'right', marginTop: 4 }}>
            <span style={{ fontSize: 11.5, color: 'var(--danger)', marginRight: 6 }}>
              Falha ao enviar{m.error ? ` — ${m.error}` : ''}
            </span>
            <button
              className="btn sec sm"
              style={{ padding: '2px 8px', fontSize: 11 }}
              onClick={() => onRetry(m.id)}
            >
              Tentar novamente
            </button>
          </div>
        )}
      </div>
      {!out && avatar}
    </div>
  )
}

export function Timeline({
  conv,
  onLinkIdentity,
  onRejectIdentity,
  onRetry,
}: {
  conv: ConversationDetailDTO
  onLinkIdentity: (identityId: string) => void
  onRejectIdentity: (identityId: string) => void
  onRetry: (messageId: string) => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [zoom, setZoom] = useState<{ url: string; caption: string | null } | null>(null)
  // The same query the history block runs; react-query serves one request to both. It is here
  // only so the scroll below re-pins once that history arrives — it lands after mount and grows
  // the thread upward, which would otherwise leave the newest message off-screen.
  /**
   * A cor de cada agente, para as iniciais de quem ainda não subiu foto.
   *
   * Vem da lista de agentes que a tela já carrega — react-query serve a mesma resposta — em vez
   * de mais um campo por mensagem: a cor é do agente, não da mensagem, e repeti-la em cada linha
   * seria carregar a mesma informação dezenas de vezes na mesma conversa.
   */
  const { data: agents = [] } = useQuery({ queryKey: qk.agents(), queryFn: api.agents })
  const agentColorOf = (id: string | null) =>
    id ? agents.find((a) => a.id === id)?.avatarColor : undefined

  const { data: history } = useQuery({
    queryKey: qk.conversationHistory(conv.id),
    queryFn: () => api.conversationHistory(conv.id),
  })
  /**
   * Rola para o fim — no elemento que de fato rola.
   *
   * No desktop é a própria timeline. No celular ela cresce livre dentro de `.conv`, que é o
   * rolador; mandar `scrollTop` para um elemento que não rola não faz nada, e a conversa abria
   * no topo, na mensagem mais velha. Descobrir qual é por medição, e não por largura de tela,
   * mantém isto certo se o CSS mudar de novo.
   */
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const rola = el.scrollHeight > el.clientHeight + 1 ? el : el.closest('.conv')
    if (rola) rola.scrollTop = rola.scrollHeight
  }, [conv.messages.length, conv.id, history?.length])

  return (
    <div className="timeline" ref={ref}>
      {zoom && <Lightbox url={zoom.url} caption={zoom.caption} onClose={() => setZoom(null)} />}
      <div className="tl-inner">
        {conv.pendingIdentities.length > 0 && (
          <IdentityMatch
            customerName={conv.customerName}
            identities={conv.pendingIdentities}
            onLink={onLinkIdentity}
            onReject={onRejectIdentity}
          />
        )}
        {/* Earlier questions from the same buyer scroll with the thread, above it. */}
        <QuestionHistory conversationId={conv.id} />
        {conv.messages.map((m, i) => (
          <Fragment key={m.id}>
            {/* Uma faixa quando o dia vira. A hora sozinha não diz se foi hoje ou em agosto, e
                numa conversa de marketplace o intervalo entre duas mensagens costuma ser de dias. */}
            {(i === 0 || diaDe(conv.messages[i - 1]!.createdAt) !== diaDe(m.createdAt)) && (
              <DaySeparator iso={m.createdAt} />
            )}
            <MessageRow
              m={m}
              onRetry={onRetry}
              onOpenImage={(url, caption) => setZoom({ url, caption })}
              customerId={conv.customerId}
              customerColor={conv.customerColor}
              customerName={conv.customerName}
              agentColor={agentColorOf(m.senderAgentId)}
            />
          </Fragment>
        ))}
        {conv.status === 'resolved' && (
          <div className="resolved-note">
            {/* "Volta se o cliente responder" é verdade em quase todo canal e mentira num quadro
                de perguntas: o comprador não tem onde responder. Uma dúvida nova dele chega como
                outra pergunta, com id próprio, e abre a própria conversa. */}
            {PUBLIC_CHANNELS.includes(conv.channelType)
              ? 'Pergunta respondida. Ela aceita apenas uma resposta — uma nova dúvida do comprador chega como outra pergunta.'
              : 'Conversa resolvida. Ela sai da caixa ativa e volta automaticamente se o cliente responder.'}
          </div>
        )}
      </div>
    </div>
  )
}
